import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";

import {
  CompanyPrimaryContact,
  HydratedCompany,
  CreateCompanyData,
  UpdateCompanyData,
  GetCompaniesResponseSchema,
  GetCompanyResponseSchema,
  CreateCompanyResponseSchema,
  UpdateCompanyResponseSchema,
} from "@forgepoint/shared";

interface CompanyLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface FetchCompaniesParams {
  search?: string;
  limit?: number;
  offset?: number;
}

interface CompaniesStore {
  companies: Map<string, HydratedCompany>;
  companyLoadingStates: Map<string, CompanyLoadingState>;
  listLoading: boolean;
  listError: string | null;

  fetchCompanies: (
    params?: FetchCompaniesParams,
  ) => Promise<{ ids: string[]; total: number }>;
  fetchCompany: (id: string) => Promise<void>;
  createCompany: (
    data: CreateCompanyData,
  ) => Promise<{ company: HydratedCompany | null; error: string | null }>;
  updateCompany: (
    id: string,
    data: UpdateCompanyData,
  ) => Promise<{ company: HydratedCompany | null; error: string | null }>;
  deleteCompany: (id: string) => Promise<boolean>;
  mergeCompanies: (companies: HydratedCompany[]) => void;
  setCompanyPrimaryContact: (
    companyId: string,
    primaryContact: CompanyPrimaryContact | null,
  ) => void;
}

const getInitialLoadingState = (): CompanyLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

export const useCompaniesStore = create<CompaniesStore>((set) => ({
  companies: new Map(),
  companyLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  fetchCompanies: async (params?: FetchCompaniesParams) => {
    set({ listLoading: true, listError: null });

    try {
      const queryParams = new URLSearchParams();
      if (params?.search) {
        queryParams.append("search", params.search);
      }
      if (params?.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }
      if (params?.offset !== undefined) {
        queryParams.append("offset", String(params.offset));
      }
      const queryString = queryParams.toString();
      const url = queryString ? `companies?${queryString}` : "companies";

      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = GetCompaniesResponseSchema.parse(response.data);
        set((state) => {
          const companiesMap = new Map(state.companies);
          data.companies.forEach((company) => {
            companiesMap.set(company.id, company);
          });
          return { companies: companiesMap, listLoading: false };
        });
        return {
          ids: data.companies.map((company) => company.id),
          total: data.total,
        };
      }

      set({ listError: "Failed to fetch companies", listLoading: false });
      return { ids: [], total: 0 };
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch companies", listLoading: false });
      return { ids: [], total: 0 };
    }
  },

  fetchCompany: async (id: string) => {
    set((state) => {
      const companyLoadingStates = new Map(state.companyLoadingStates);
      const currentState =
        companyLoadingStates.get(id) || getInitialLoadingState();
      companyLoadingStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { companyLoadingStates };
    });

    try {
      const response = await fetchApi(`companies/${id}`, "GET");

      if (response.ok && response.data) {
        const data = GetCompanyResponseSchema.parse(response.data);
        const company = data.company;
        set((state) => {
          const companies = new Map(state.companies);
          const companyLoadingStates = new Map(state.companyLoadingStates);
          const currentState =
            companyLoadingStates.get(id) || getInitialLoadingState();
          companies.set(id, company);
          companyLoadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return { companies, companyLoadingStates };
        });
        return;
      }

      set((state) => {
        const companyLoadingStates = new Map(state.companyLoadingStates);
        const currentState =
          companyLoadingStates.get(id) || getInitialLoadingState();
        companyLoadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch company",
        });
        return { companyLoadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const companyLoadingStates = new Map(state.companyLoadingStates);
        const currentState =
          companyLoadingStates.get(id) || getInitialLoadingState();
        companyLoadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch company",
        });
        return { companyLoadingStates };
      });
    }
  },

  createCompany: async (data: CreateCompanyData) => {
    try {
      const response = await fetchApi("companies", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = CreateCompanyResponseSchema.parse(response.data);
        const company = result.company;
        set((state) => {
          const companies = new Map(state.companies);
          companies.set(company.id, company);
          return { companies };
        });
        return { company, error: null };
      }

      return {
        company: null,
        error: response.error ?? "Failed to create company",
      };
    } catch (error) {
      captureException(error as Error);
      return { company: null, error: "Failed to create company" };
    }
  },

  updateCompany: async (id: string, data: UpdateCompanyData) => {
    set((state) => {
      const companyLoadingStates = new Map(state.companyLoadingStates);
      const currentState =
        companyLoadingStates.get(id) || getInitialLoadingState();
      companyLoadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { companyLoadingStates };
    });

    try {
      const response = await fetchApi(`companies/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = UpdateCompanyResponseSchema.parse(response.data);
        const company = result.company;
        set((state) => {
          const companies = new Map(state.companies);
          const companyLoadingStates = new Map(state.companyLoadingStates);
          const currentState =
            companyLoadingStates.get(id) || getInitialLoadingState();
          companies.set(id, company);
          companyLoadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { companies, companyLoadingStates };
        });
        return { company: result.company, error: null };
      }

      const errorMessage = response.error ?? "Failed to update company";
      set((state) => {
        const companyLoadingStates = new Map(state.companyLoadingStates);
        const currentState =
          companyLoadingStates.get(id) || getInitialLoadingState();
        companyLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { companyLoadingStates };
      });
      return { company: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const companyLoadingStates = new Map(state.companyLoadingStates);
        const currentState =
          companyLoadingStates.get(id) || getInitialLoadingState();
        companyLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update company",
        });
        return { companyLoadingStates };
      });
      return { company: null, error: "Failed to update company" };
    }
  },

  deleteCompany: async (id: string) => {
    set((state) => {
      const companyLoadingStates = new Map(state.companyLoadingStates);
      const currentState =
        companyLoadingStates.get(id) || getInitialLoadingState();
      companyLoadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { companyLoadingStates };
    });

    try {
      const response = await fetchApi(`companies/${id}`, "DELETE");

      if (response.ok) {
        set((state) => {
          const companies = new Map(state.companies);
          const companyLoadingStates = new Map(state.companyLoadingStates);
          companies.delete(id);
          companyLoadingStates.delete(id);
          return { companies, companyLoadingStates };
        });
        return true;
      }

      set((state) => {
        const companyLoadingStates = new Map(state.companyLoadingStates);
        const currentState =
          companyLoadingStates.get(id) || getInitialLoadingState();
        companyLoadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete company",
        });
        return { companyLoadingStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const companyLoadingStates = new Map(state.companyLoadingStates);
        const currentState =
          companyLoadingStates.get(id) || getInitialLoadingState();
        companyLoadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete company",
        });
        return { companyLoadingStates };
      });
      return false;
    }
  },

  // Preserves an already-hydrated primaryContact when the incoming company
  // (e.g. a quote-embedded one) does not carry the field.
  mergeCompanies: (companiesToMerge: HydratedCompany[]) => {
    set((state) => {
      const companies = new Map(state.companies);
      companiesToMerge.forEach((company) => {
        const existing = companies.get(company.id);
        companies.set(company.id, {
          ...company,
          primaryContact:
            company.primaryContact !== undefined
              ? company.primaryContact
              : (existing?.primaryContact ?? null),
        });
      });
      return { companies };
    });
  },

  setCompanyPrimaryContact: (
    companyId: string,
    primaryContact: CompanyPrimaryContact | null,
  ) => {
    set((state) => {
      const company = state.companies.get(companyId);
      if (!company) {
        return state;
      }
      const companies = new Map(state.companies);
      companies.set(companyId, { ...company, primaryContact });
      return { companies };
    });
  },
}));
